import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Button,
} from '@mui/material';
import {
  Public,
  LocationOn,
  RocketLaunch,
  TrendingUp,
  ArrowForward,
  Language,
  Security,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Coverage = () => {
  const navigate = useNavigate();

  const expansionPhases = [
    {
      phase: 'Phase 1',
      title: 'Kingdom of Eswatini',
      desc: 'Our home base — building the foundation of a secure, modern internet network across the Kingdom.',
      icon: <LocationOn />,
      color: '#e60108',
      status: 'In Progress',
    },
    {
      phase: 'Phase 2',
      title: 'Southern Africa',
      desc: 'Expanding connectivity to neighboring countries across the Southern African region.',
      icon: <TrendingUp />,
      color: '#191919',
      status: 'Coming Soon',
    },
    {
      phase: 'Phase 3',
      title: 'African Continent',
      desc: 'Scaling our secure internet infrastructure to reach businesses and communities across Africa.',
      icon: <Public />,
      color: '#e60108',
      status: 'Future Vision',
    },
   
  ];

  const highlights = [
    { text: 'Secure encrypted connections', icon: <Security sx={{ fontSize: 20 }} /> },
    { text: 'Reliable uptime', icon: <CheckCircle sx={{ fontSize: 20 }} /> },
    { text: 'Built for local communities', icon: <Language sx={{ fontSize: 20 }} /> },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Telecom Tower Infrastructure.jpg"
          alt="Global Coverage"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Our Coverage"
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                display: 'inline-flex',
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Secure Internet,
              <br />
              
            </Typography>
         
          </motion.div>
        </Container>
      </Box>

      {/* Coverage Image Section */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            
            <Typography
              variant="h2"
              sx={{ mb: 2, fontWeight: 800, color: '#191919' }}
            >
             Where We Are Today
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                maxWidth: 720,
                mx: 'auto',
                mb: 5,
                lineHeight: 1.8,
                fontSize: '1.05rem',
              }}
            >
              Eswatini is where our story begins. We're laying the groundwork for a
              secure, modern internet network designed to serve local businesses,
              communities, and families — with the ambition to grow far beyond our
              borders.
            </Typography>

            <Box
              component="img"
              src="/images/coverage.png"
              alt="Coverage in the Kingdom of Eswatini"
              sx={{
                width: '100%',
                maxWidth: 1100,
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              }}
            />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 5 }}
            >
              {highlights.map((item, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Box sx={{ color: '#e60108', display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#191919', fontWeight: 500 }}>
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Expansion Roadmap */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Roadmap"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              From Eswatini to the World
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 720, mx: 'auto' }}
            >
              A phased journey to bring secure internet to Southern Africa, the
              continent, and beyond
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {expansionPhases.map((phase, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Chip
                      label={phase.phase}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: phase.color + '15',
                        color: phase.color,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                      }}
                    />
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: phase.color + '15',
                        color: phase.color,
                        mb: 2,
                        mx: 'auto',
                      }}
                    >
                      {phase.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ color: '#191919', textAlign: 'center' }}
                    >
                      {phase.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#a0a0a0',
                        textAlign: 'center',
                        lineHeight: 1.6,
                        mb: 2,
                      }}
                    >
                      {phase.desc}
                    </Typography>
                    <Chip
                      label={phase.status}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 'auto',
                        borderColor: phase.color + '40',
                        color: phase.color,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                      }}
                    />
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{
                  bgcolor: '#e60108',
                  px: 5,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: '#b80006',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Get Connected
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Coverage;